/** @format */

import Alert from '@mui/material/Alert';
import {styled} from '@mui/material/styles';
import {Box, Grid, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import JuliaBot from 'components/managers/JuliaBot';
import Loading from 'components/loading/Loading';
import ModalPage from 'components/ModalPage';
import PaymentMethod from 'pages/Home/Settings/PaymentMethods/PaymentMethod';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import {formatRUB} from 'helpers/numberHelpers';

import {useChargePostpayBillMutation, useGetCurrentUserQuery} from './graphql/__generated__';

const PREFIX = 'ConfirmPostpayBudget';

const classes = {
  budgetText: `${PREFIX}-budgetText`,
  body: `${PREFIX}-body`,
};

const Root = styled(Box)(({theme}) => ({
  [`& .${classes.budgetText}`]: {
    color: '#4A4A4A',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '18px',
  },

  [`& .${classes.body}`]: {
    color: theme.palette.text.secondary,
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '20px',
  },
}));

const ConfirmPostpayBudget = () => {
  const {t} = useTranslation();

  const {loading, error, data} = useGetCurrentUserQuery();
  const [chargePostpayBill, {loading: chargePostpayBillLoading}] = useChargePostpayBillMutation();

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser) {
    return <Loading />;
  }

  const {budget, paymentMethods} = data.currentUser;

  const activePaymentMethod = paymentMethods.find(v => !v.isExpired);

  const renderButton = () => {
    const handleChargePostpayBill = async () => {
      const paymentMethodId = activePaymentMethod?.id ?? -1;

      const {data: chargePostpayBillData} = await chargePostpayBill({
        variables: {paymentMethodId},
      });

      if (!chargePostpayBillData) {
        throw new Error('chargePostpayBillData not found.');
      }

      const {confirmationUrl} = chargePostpayBillData.chargePostpayBill;
      window.location.href = confirmationUrl ?? window.location.href;
    };

    return (
      <Grid container>
        <Grid item xs={12} sm={6}>
          <PrimaryMediumButton
            disabled={chargePostpayBillLoading}
            fullWidth
            onClick={handleChargePostpayBill}
          >
            <Trans>Оплатить</Trans>
          </PrimaryMediumButton>
        </Grid>
      </Grid>
    );
  };

  const formattedBudget = formatRUB(budget ?? 0);
  return (
    <Root>
      <ModalPage
        header={t(
          'Оплата подписки за завершенный период использования',
          'Оплата подписки за завершенный период использования',
        )}
      >
        <Box
          sx={{
            mt: 3,
          }}
        >
          <Typography className={classes.budgetText}>
            <Trans>Ваш бюджет {{formattedBudget}}</Trans>
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 10,
          }}
        >
          <Typography className={classes.body}>
            <Trans>Если возникли трудности с оплатой, свяжитесь с менеджером AdSensor</Trans>
          </Typography>
          <Box
            sx={{
              mt: 3,
            }}
          >
            <JuliaBot buttonVariant='SECONDARY' variant='LIGHT' />
          </Box>
        </Box>
        <Box
          sx={{
            mt: 10,
          }}
        >
          {activePaymentMethod && (
            <Box
              sx={{
                mb: 3,
              }}
            >
              <PaymentMethod paymentMethod={activePaymentMethod} variant='LIGHT' />
            </Box>
          )}

          {renderButton()}
        </Box>
      </ModalPage>
    </Root>
  );
};

export default ConfirmPostpayBudget;
